import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AnimatePresence, motion } from "framer-motion";
import LandingPage from "./pages/LandingPage";
import PhotoPage from "./pages/PhotoPage";
import FilmPage from "./pages/FilmPage";
import FilmLandingPage from "./pages/FilmLandingPage";
import CollectionPage from "./pages/CollectionPage";
import CollectionsListPage from "./pages/CollectionsListPage";
import AboutPage from "./pages/AboutPage";
import InquirePage from "./pages/InquirePage";
import AdminDashboard from "./pages/admin/AdminDashboard";
import ServicesPage from "./pages/ServicesPage";
import ServiceDetailPage from "./pages/ServiceDetailPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import CrystalDifferencePage from "./pages/CrystalDifferencePage";
import InspoPage from "./pages/InspoPage";
import InspoSavedPage from "./pages/InspoSavedPage";
import InspoListPublicPage from "./pages/InspoListPublicPage";
import PartnersPage from "./pages/PartnersPage";
import VendorsPage from "./pages/VendorsPage";
import VendorDetailPage from "./pages/VendorDetailPage";
import ReviewsPage from "./pages/ReviewsPage";
import SeoLandingPage from "./pages/SeoLandingPage";
import SaveToastHost from "./components/SaveToastHost";
import GrainOverlay from "./components/GrainOverlay";
import { trackPageView } from "./utils/fbPixel";

// Component to track page views on route changes
function FacebookPixelTracker() {
  const location = useLocation();
  
  useEffect(() => {
    // Track page view on every route change
    trackPageView();
  }, [location.pathname]);
  
  return null;
}

// Fade between routes (opacity only — keeps fixed headers/overlays positioned correctly)
function AnimatedRoutes() {
  const location = useLocation();

  useEffect(() => {
    if (!window.location.hash) window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
        data-testid="page-transition"
      >
        <Routes location={location}>
          {/* Landing Page */}
          <Route path="/" element={<LandingPage />} />
          
          {/* Photo Section - Art Gallery Experience */}
          <Route path="/photo" element={<PhotoPage />} />
          
          {/* Film Section - Landing Page */}
          <Route path="/film" element={<FilmLandingPage />} />
          
          {/* Film Theatre - Immersive IMAX Experience */}
          <Route path="/film/theatre" element={<FilmPage />} />
          
          {/* Crystal Difference - Why Us */}
          <Route path="/crystal-difference" element={<CrystalDifferencePage />} />
          
          {/* Collection Page */}
          <Route path="/collection/:id" element={<CollectionPage />} />
          
          {/* All Collections Page */}
          <Route path="/collections" element={<CollectionsListPage />} />
          
          {/* About Page - Photo Style */}
          <Route path="/about" element={<AboutPage />} />

          {/* Reviews */}
          <Route path="/reviews" element={<ReviewsPage />} />
          
          {/* Inquire Page - Photo Style */}
          <Route path="/inquire" element={<InquirePage />} />
          
          {/* Services Pages */}
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/:slug" element={<ServiceDetailPage />} />
          
          {/* Blog Pages */}
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />

          {/* Inspo — TikTok-style feed + saved boards */}
          <Route path="/inspo" element={<InspoPage />} />
          <Route path="/inspo/saved" element={<InspoSavedPage />} />
          <Route path="/inspo/list/:slug" element={<InspoListPublicPage />} />

          {/* Partners Referral Program */}
          <Route path="/partners" element={<PartnersPage />} />

          {/* Trusted Vendors directory */}
          <Route path="/vendors" element={<VendorsPage />} />
          <Route path="/vendors/:slug" element={<VendorDetailPage />} />

          {/* Admin Dashboard */}
          <Route path="/admin/dashboard" element={<AdminDashboard />} />

          {/* SEO landing pages (city / culture) — must stay last */}
          <Route path="/:slug" element={<SeoLandingPage />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <FacebookPixelTracker />
          <SaveToastHost />
          <GrainOverlay />
          <AnimatedRoutes />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
